import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Input,
  Textarea,
  Switch,
  Divider,
  Tabs,
  Tab,
  Spinner,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Select,
  SelectItem,
} from "@heroui/react";
import {
  Save,
  Store,
  CreditCard,
  TrendingUp,
  Truck,
  Clock,
  Mail,
  Phone,
  Globe,
  Trash,
  Plus,
  Calendar,
  Edit,
} from "lucide-react";

import { configService } from "@/services/configService";
import { useTheme } from "@/context/ThemeContext";
//@ts-ignore
// Interface para el horario semanal
interface WeekSchedule {
  id: number;
  type: "delivery" | "pickup";
  day_of_week: number;
  open_hour: string;
  close_hour: string;
}
//@ts-ignore
// Interface para los datos del formulario de horarios semanales
interface WeekScheduleFormData {
  type: "delivery" | "pickup";
  day_of_week: number;
  open_hour: string;
  close_hour: string;
}
//@ts-ignore
// Interface para los días de la semana
interface DayOfWeek {
  value: number;
  label: string;
}
//@ts-ignore
// Interface para los tipos de horarios
interface ScheduleType {
  value: "delivery" | "pickup";
  label: string;
}

// Interface para las horas de operación
interface HoursOfOperation {
  open: string;
  close: string;
}

// Interface para días especiales
interface SpecialDay {
  date: string;
  open?: string;
  close?: string;
  pickup_open?: string;
  pickup_close?: string;
  isClosed?: boolean;
}

// Interface para la configuración del restaurante
interface RestaurantConfig {
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  phone: string;
  email: string;
  website: string;
  tax_rate: string;
  logo_url: string;
  hours_of_operation: {
    monday: HoursOfOperation;
    tuesday: HoursOfOperation;
    wednesday: HoursOfOperation;
    thursday: HoursOfOperation;
    friday: HoursOfOperation;
    saturday: HoursOfOperation;
    sunday: HoursOfOperation;
  };
  pickup_hours?: {
    monday?: HoursOfOperation;
    tuesday?: HoursOfOperation;
    wednesday?: HoursOfOperation;
    thursday?: HoursOfOperation;
    friday?: HoursOfOperation;
    saturday?: HoursOfOperation;
    sunday?: HoursOfOperation;
  };
  special_days: SpecialDay[];
  delivery_radius: number;
  minimum_delivery_amount: number;
  delivery_fee: number;
}

// Días de la semana en español, con su valor numérico correspondiente
const daysOfWeek = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 0, label: "Sunday" },
];

// Tipos de horarios disponibles
const types = [
  { value: "delivery", label: "Delivery" },
  { value: "pickup", label: "Pickup" },
];

// Estado inicial del formulario de horario
const initialSchedule = {
  type: "delivery",
  day_of_week: 1,
  open_hour: "09:00",
  close_hour: "18:00",
};

// Tipos para TypeScript
// Interfaces para tipos
//@ts-ignore
const WeekSchedule = {
  id: 0,
  type: "",
  day_of_week: 0,
  open_hour: "",
  close_hour: "",
};

// Componente para gestionar los horarios semanales
const WeekScheduleManager = () => {
  const [schedules, setSchedules] = useState<any[]>([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialSchedule);
  const [activeTab, setActiveTab] = useState("delivery");
  const modal = useDisclosure();

  // Función para obtener los horarios desde el servicio de configuración
  const fetchSchedules = async () => {
    const data = await configService.getWeekSchedule();

    setSchedules(data);
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  // Manejador de cambios en selectores
  const handleSelectChange = (name:any) => (selection:any) => {
    const value =
      typeof selection === "string" ? selection : Array.from(selection)[0];

    setForm({
      ...form,
      [name]: name === "day_of_week" ? Number(value) : value,
    });
  };

  // Manejador de cambios en inputs
  const handleInputChange = (name:any, value:any) => {
    setForm({
      ...form,
      [name]: name === "day_of_week" ? Number(value) : value,
    });
  };

  // Manejador para editar un horario existente
  const handleEdit = (schedule:any) => {
    setEditing(schedule.id);
    setForm({
      type: schedule.type,
      open_hour: schedule.open_hour.slice(0, 5),
      close_hour: schedule.close_hour.slice(0, 5),
      day_of_week: schedule.day_of_week,
    });
    modal.onOpen();
  };

  // Manejador para eliminar un horario
  const handleDelete = async (id:any) => {
    await configService.deleteWeekSchedule(id);
    fetchSchedules();
  };

  // Manejador para enviar el formulario (editar o crear)
  const handleSubmit = async () => {
    if (editing) {
      //@ts-ignore
      await configService.updateWeekSchedule(editing, form);
    } else {
      //@ts-ignore
      await configService.createWeekSchedule(form);
    }
    setEditing(null);
    setForm(initialSchedule);
    modal.onClose();
    fetchSchedules();
  };

  // Filtrar horarios por tipo (delivery o pickup)
  const filteredSchedules = schedules.filter(
    (schedule) => schedule.type === activeTab,
  );

  // Formatear hora para mostrar AM/PM
  const formatHour = (hour:number) => {
    const ampm = hour >= 12 ? "PM" : "AM";
    const h = hour > 12 ? hour - 12 : hour;

    return `${h}${ampm}`;
  };

  // Generar calendario visual de horarios
  const renderCalendar = () => {
    // Agrupar horarios por día
    const schedulesByDay: { [key: number]: WeekSchedule[] } = {};

    filteredSchedules.forEach((schedule) => {
      if (!schedulesByDay[schedule.day_of_week]) {
        schedulesByDay[schedule.day_of_week] = [];
      }
      schedulesByDay[schedule.day_of_week].push(schedule);
    });

    // Encontrar el rango de horas para mostrar en el calendario
    let minHour = 8; // Hora mínima predeterminada
    let maxHour = 23; // Hora máxima predeterminada
    filteredSchedules.forEach((schedule) => {
      const openHour = parseInt(schedule.open_hour.split(":")[0]);
      const closeHour = parseInt(schedule.close_hour.split(":")[0]);
      if (openHour < minHour) minHour = openHour;
      if (closeHour > maxHour) maxHour = closeHour;
    });

    // Asegurar un rango mínimo de horas para mostrar
    if (maxHour - minHour < 5) maxHour = minHour + 5;

    // Generar el rango de horas a mostrar
    const hoursToShow = Array.from(
      { length: maxHour - minHour + 1 },
      (_, i) => i + minHour,
    );

    return (
      <div className="overflow-x-auto mt-4">
        <div className="min-w-full bg-white dark:bg-default-100 border border-gray-200 rounded-md">
          {/* Header con días */}
          <div className="grid grid-cols-8 border-b">
            <div className="p-2 border-r w-16" />
            {daysOfWeek.map((day) => (
              <div
                key={day.value}
                className="p-2 text-center font-medium border-r"
              >
                {day.label}
              </div>
            ))}
          </div>
          {/* Rows for each hour */}
          {hoursToShow.map((hour) => (
            <div key={hour} className="grid grid-cols-8 border-b">
              {/* Columna de hora */}
              <div className="p-2 border-r text-right font-medium w-16">
                {formatHour(hour)}
              </div>
              {/* Columnas para cada día */}
              {daysOfWeek.map((day) => {
                const daySchedules = schedulesByDay[day.value] || [];
                return (
                  <div key={day.value} className="border-r relative h-16">
                    {daySchedules.map((schedule) => {
                      const openHour = parseInt(
                        schedule.open_hour.split(":")[0],
                      );
                      const openMinute = parseInt(
                        schedule.open_hour.split(":")[1],
                      );
                      const closeHour = parseInt(
                        schedule.close_hour.split(":")[0],
                      );
                      const closeMinute = parseInt(
                        schedule.close_hour.split(":")[1],
                      );
                      // Calcular si este bloque debe mostrarse en esta hora
                      const isStartingInThisHour = openHour === hour;
                      //@ts-ignore
                      const isActiveInThisHour =
                        (hour >= openHour && hour < closeHour) ||
                        (hour === openHour && hour === closeHour - 1);
                      if (isStartingInThisHour) {
                        // Calcular altura del bloque en base a la duración
                        const durationInHours = closeHour - openHour;
                        //@ts-ignore
                        const durationInMinutes =
                          durationInHours * 60 + closeMinute - openMinute;
                          //@ts-ignore
                        const heightPercentage = Math.min(
                          100 * durationInHours,
                          100 * (maxHour - hour),
                        );
                        return (
                          <div
                            key={schedule.id}
                            className="absolute left-0 right-0 mx-1 bg-blue-400 rounded p-2 text-white overflow-hidden"
                            style={{
                              top: "0.25rem",
                              bottom: durationInHours > 1 ? "auto" : "0.25rem",
                              height:
                                durationInHours > 1
                                  ? `${64 * durationInHours - 4}px`
                                  : "auto",
                              zIndex: 10,
                              backgroundColor:
                                schedule.type === "delivery"
                                  ? "#4a89dc"
                                  : "#e67e22",
                            }}
                          >
                            <div className="flex justify-between items-start">
                              <div className="overflow-hidden">
                                <div className="font-semibold text-sm whitespace-nowrap">
                                  {schedule.open_hour.slice(0, 5)} -{" "}
                                  {schedule.close_hour.slice(0, 5)}
                                </div>
                                <div className="text-xs">{schedule.type}</div>
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                <button
                                  className="text-white bg-black bg-opacity-20 rounded p-1"
                                  onClick={() => handleEdit(schedule)}
                                >
                                  <Edit size={12} />
                                </button>
                                <button
                                  className="text-white bg-red-500 bg-opacity-20 rounded p-1"
                                  onClick={() => handleDelete(schedule.id)}
                                >
                                  <Trash size={12} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return null;
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card className="p-4 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-lg font-medium flex items-center gap-2">
          <Clock size={18} /> Weekly Schedules
        </h4>
        <Button
          color="primary"
          startContent={<Plus size={16} />}
          onPress={modal.onOpen}
        >
          Add Schedule
        </Button>
      </div>
      <Tabs
        aria-label="Options"
        selectedKey={activeTab}
        color="primary"
        variant="bordered"
        //@ts-ignore
        onSelectionChange={setActiveTab}
      >
        <Tab key="delivery" title="Delivery">
          {renderCalendar()}
        </Tab>
        <Tab key="pickup" title="Pickup">
          {renderCalendar()}
        </Tab>
      </Tabs>
      <Modal isOpen={modal.isOpen} onClose={modal.onClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>
                {editing ? "Edit Schedule" : "Add Schedule"}
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <Select
                    label="Type"
                    selectedKeys={new Set([form.type])}
                    onSelectionChange={(selection) =>
                      handleSelectChange("type")(Array.from(selection)[0])
                    }
                  >
                    {types.map((t) => (
                      <SelectItem key={t.value}>{t.label}</SelectItem>
                    ))}
                  </Select>
                  <Select
                    label="Day"
                    selectedKeys={new Set([form.day_of_week.toString()])}
                    onSelectionChange={(selection) =>
                      handleSelectChange("day_of_week")(
                        Array.from(selection)[0],
                      )
                    }
                  >
                    {daysOfWeek.map((d) => (
                      <SelectItem key={d.value}>{d.label}</SelectItem>
                    ))}
                  </Select>
                  <Input
                    type="time"
                    label="Opens"
                    value={form.open_hour}
                    onChange={(e) =>
                      handleInputChange("open_hour", e.target.value)
                    }
                  />
                  <Input
                    type="time"
                    label="Closes"
                    value={form.close_hour}
                    onChange={(e) =>
                      handleInputChange("close_hour", e.target.value)
                    }
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button color="primary" onPress={handleSubmit}>
                  Save
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </Card>
  );
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const { theme, setTheme } = useTheme();
  const [editingDay, setEditingDay] = useState<keyof RestaurantConfig["hours_of_operation"]>("monday");
  const [editingSpecialDay, setEditingSpecialDay] = useState<SpecialDay | null>(
    null,
  );

  // Modales para edición
  const timeSlotModal = useDisclosure();
  const specialDayModal = useDisclosure();

  // Configuración del restaurante
  const [restaurantConfig, setRestaurantConfig] = useState<RestaurantConfig>({
    name: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    phone: "",
    email: "",
    website: "",
    tax_rate: "8.00",
    logo_url: "",
    //@ts-ignore
    hours_of_operation: {},
    special_days: [],
    delivery_radius: 5,
    minimum_delivery_amount: 10,
    delivery_fee: 2.5,
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  // Obtener la configuración del restaurante
  const fetchConfig = async () => {
    try {
      setLoading(true);
      const config = await configService.getRestaurantConfig();
      const hoursData =
        typeof config.hours_of_operation === "string"
          ? JSON.parse(config.hours_of_operation)
          : config.hours_of_operation;
      let deliveryHours = {};
      let pickupHours = {};
      if (hoursData && typeof hoursData === "object") {
        if (hoursData.delivery) {
          deliveryHours = hoursData.delivery;
        }
        if (hoursData.pickup) {
          pickupHours = hoursData.pickup;
        }
      }
      setRestaurantConfig({
        ...config,
        tax_rate: config.tax_rate.toString(),
        //@ts-ignore
        minimum_delivery_amount: config.minimum_delivery_amount / 100,
        //@ts-ignore
        delivery_fee: config.delivery_fee / 100,
        //@ts-ignore
        hours_of_operation: deliveryHours,
        pickup_hours: pickupHours,
        //@ts-ignore//@ts-ignore
        special_days: config.special_days || [],
      });
    } catch (err) {
      console.error("Error loading configuration:", err);
      setError("Could not load restaurant configuration");
    } finally {
      setLoading(false);
    }
  };

  // Manejador de cambios en inputs
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;

    if (type === "number") {
      setRestaurantConfig({
        ...restaurantConfig,
        [name]: parseFloat(value) || 0,
      });
    } else {
      setRestaurantConfig({
        ...restaurantConfig,
        [name]: value,
      });
    }
  };

  // Manejador para cambiar las horas de operación
  const handleTimeChange = (day: string, timeType: string, value: string) => {
    setRestaurantConfig({
      ...restaurantConfig,
      hours_of_operation: {
        ...restaurantConfig.hours_of_operation,
        [day]: {
          //@ts-ignore
          ...restaurantConfig.hours_of_operation[day],
          [timeType]: value,
        },
      },
    });
  };

  // Manejador para cambiar las horas de recogida
  const handlePickupTimeChange = (
    day: string,
    timeType: string,
    value: string,
  ) => {
    setRestaurantConfig({
      ...restaurantConfig,
      pickup_hours: {
        ...(restaurantConfig.pickup_hours || {}),
        [day]: {
          //@ts-ignore
          ...(restaurantConfig.pickup_hours?.[day] || {}),
          [timeType]: value,
        },
      },
    });
  };

  // Manejador para cambiar los días especiales
  const handleSpecialDayChange = (
    index: number,
    field: string,
    value: string | boolean,
  ) => {
    const updatedSpecialDays = restaurantConfig.special_days.map((day, i) =>
      i === index ? { ...day, [field]: value } : day,
    );

    setRestaurantConfig({
      ...restaurantConfig,
      special_days: updatedSpecialDays,
    });
  };

  // Añadir un día especial
  const handleAddSpecialDay = () => {
    const tomorrow = new Date();

    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateString = tomorrow.toISOString().split("T")[0];

    setEditingSpecialDay({
      date: dateString,
      open: "09:00",
      close: "18:00",
      isClosed: false,
    });
    specialDayModal.onOpen();
  };

  // Guardar un día especial
  const handleSaveSpecialDay = () => {
    if (!editingSpecialDay) return;
    setRestaurantConfig({
      ...restaurantConfig,
      special_days: [...restaurantConfig.special_days, editingSpecialDay],
    });
    specialDayModal.onClose();
    setEditingSpecialDay(null);
  };

  // Eliminar un día especial
  const handleRemoveSpecialDay = (index: number) => {
    const updatedSpecialDays = restaurantConfig.special_days.filter(
      (_, i) => i !== index,
    );

    setRestaurantConfig({
      ...restaurantConfig,
      special_days: updatedSpecialDays,
    });
  };

  // Guardar la configuración del restaurante
  const handleSaveConfig = async () => {
    try {
      if (!restaurantConfig.name) {
        setError("Restaurant name is required");
        return;
      }
      setSaving(true);
      setError("");
      setSuccessMessage("");
      const combinedHours = {
        delivery: restaurantConfig.hours_of_operation,
        pickup: restaurantConfig.pickup_hours || {},
      };
      const apiData = {
        ...restaurantConfig,
        tax_rate: parseFloat(restaurantConfig.tax_rate),
        minimum_delivery_amount: Math.round(
          restaurantConfig.minimum_delivery_amount * 100,
        ),
        delivery_fee: Math.round(restaurantConfig.delivery_fee * 100),
        hours_of_operation: JSON.stringify(combinedHours),
      };
      await configService.updateRestaurantConfig(apiData);
      setSuccessMessage("Configuration saved successfully");
    } catch (err) {
      console.error("Error saving configuration:", err);
      setError("Could not save configuration");
    } finally {
      setSaving(false);
    }
  };

  // Navegar a métodos de pago
  const handleNavigateToPaymentMethods = () => {
    navigate("/settings/payment");
  };

  // Editar un día específico
  //@ts-ignore
  const handleEditDay = (day: any) => {
    setEditingDay(day);
    timeSlotModal.onOpen();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Settings</h1>
        <Button
          color="primary"
          isLoading={saving}
          startContent={<Save size={16} />}
          onPress={handleSaveConfig}
        >
          Save Configuration
        </Button>
      </div>
      {error && (
        <div className="bg-danger-50 text-danger p-3 rounded-md">{error}</div>
      )}
      {successMessage && (
        <div className="bg-success-50 text-success p-3 rounded-md">
          {successMessage}
        </div>
      )}
      <Tabs>
        <Tab key="general" title="General">
          <Card className="p-6 mt-4">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Store size={20} />
              Restaurant Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Restaurant Name"
                name="name"
                value={restaurantConfig.name}
                onChange={handleInputChange}
              />
              <Input
                label="Email"
                name="email"
                startContent={<Mail className="text-default-400" size={16} />}
                type="email"
                value={restaurantConfig.email}
                onChange={handleInputChange}
              />
              <Input
                label="Phone"
                name="phone"
                startContent={<Phone className="text-default-400" size={16} />}
                value={restaurantConfig.phone}
                onChange={handleInputChange}
              />
              <Input
                label="Website"
                name="website"
                startContent={<Globe className="text-default-400" size={16} />}
                value={restaurantConfig.website}
                onChange={handleInputChange}
              />
              <div className="md:col-span-2">
                <Textarea
                  label="Address"
                  name="address"
                  value={restaurantConfig.address}
                  onChange={handleInputChange}
                />
              </div>
              <Input
                label="City"
                name="city"
                value={restaurantConfig.city}
                onChange={handleInputChange}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="State/Province"
                  name="state"
                  value={restaurantConfig.state}
                  onChange={handleInputChange}
                />
                <Input
                  label="Zip Code"
                  name="zip_code"
                  value={restaurantConfig.zip_code}
                  onChange={handleInputChange}
                />
              </div>
              <div className="md:col-span-2">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Clock className="mr-2" size={20} />
                  Operating Hours
                </h3>
                <WeekScheduleManager />
                <Card className="p-4 dark:bg-gray-800">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-lg font-medium">Special Days</h4>
                    <Button
                      className="bg-blue-600"
                      color="primary"
                      startContent={<Plus size={16} />}
                      onPress={handleAddSpecialDay}
                    >
                      Add Special Day
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {restaurantConfig.special_days &&
                    restaurantConfig.special_days.length > 0 ? (
                      restaurantConfig.special_days.map((specialDay, index) => (
                        <div
                          key={index}
                          className="relative p-4 border border-gray-300 rounded-lg dark:border-gray-700"
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <h5 className="text-base font-medium mb-0">
                                Date:{" "}
                                {new Date(specialDay.date).toLocaleDateString(
                                  "en-US",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  },
                                )}
                              </h5>
                              <Button
                                isIconOnly
                                className="p-0"
                                color="default"
                                size="sm"
                                variant="light"
                              >
                                <input
                                  className="absolute inset-0 opacity-0 cursor-pointer w-8 h-8"
                                  type="date"
                                  value={specialDay.date}
                                  onChange={(e) =>
                                    handleSpecialDayChange(
                                      index,
                                      "date",
                                      e.target.value,
                                    )
                                  }
                                />
                                <span className="text-gray-400">
                                  <Calendar size={18} />
                                </span>
                              </Button>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-base">Closed:</span>
                              <Switch
                                className="ml-1"
                                isSelected={specialDay.isClosed}
                                onChange={() =>
                                  handleSpecialDayChange(
                                    index,
                                    "isClosed",
                                    !specialDay.isClosed,
                                  )
                                }
                              />
                              <Button
                                isIconOnly
                                className="ml-2"
                                color="danger"
                                variant="light"
                                onPress={() => handleRemoveSpecialDay(index)}
                              >
                                <Trash size={18} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        No special days configured
                      </div>
                    )}
                  </div>
                </Card>
              </div>
              <Divider className="my-6 md:col-span-2" />
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 md:col-span-2">
                <TrendingUp size={20} />
                Tax Configuration
              </h2>
              <Input
                type="number"
                label="Tax Rate (%)"
                name="tax_rate"
                step="0.01"
                value={restaurantConfig.tax_rate}
                onChange={handleInputChange}
                min="0"
                max="100"
                description="Tax percentage applied to all orders"
              />
              <Divider className="my-6 md:col-span-2" />
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 md:col-span-2">
                <Truck size={20} />
                Delivery Configuration
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:col-span-2">
                <Input
                  type="number"
                  label="Delivery Radius (km)"
                  name="delivery_radius"
                  value={restaurantConfig.delivery_radius.toString()}
                  onChange={handleInputChange}
                  min="0"
                  description="Maximum distance for deliveries"
                />
                <Input
                  type="number"
                  label="Minimum Order ($)"
                  name="minimum_delivery_amount"
                  value={restaurantConfig.minimum_delivery_amount.toString()}
                  onChange={handleInputChange}
                  min="0"
                  startContent={
                    <div className="pointer-events-none flex items-center">
                      <span className="text-default-400 text-small">$</span>
                    </div>
                  }
                  description="Minimum order amount for deliveries"
                />
                <Input
                  type="number"
                  label="Delivery Fee ($)"
                  name="delivery_fee"
                  value={restaurantConfig.delivery_fee.toString()}
                  onChange={handleInputChange}
                  min="0"
                  startContent={
                    <div className="pointer-events-none flex items-center">
                      <span className="text-default-400 text-small">$</span>
                    </div>
                  }
                  description="Standard delivery fee"
                />
              </div>
            </div>
          </Card>
        </Tab>
        {/* Otras pestañas omitidas por brevedad */}
        <Tab key="payment" title="Payment Methods">
          <Card className="p-6 mt-4 space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <CreditCard size={20} />
                Payment Methods
              </h2>
              <Button color="primary" onPress={handleNavigateToPaymentMethods}>
                Manage Payment Methods
              </Button>
            </div>
            <p className="text-default-500">
              Configure the payment methods accepted by your restaurant.
            </p>
            <Divider />
            <p>On the payment methods management page, you can:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>View all available payment methods</li>
              <li>Add new payment methods</li>
              <li>Edit existing methods</li>
              <li>Configure card information requirements</li>
            </ul>
          </Card>
        </Tab>
        <Tab key="notifications" title="Notifications">
          <Card className="p-6 mt-4 space-y-4">
            <h2 className="text-xl font-semibold mb-4">
              Notification Settings
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-default-500">
                    Receive notifications via email about new orders
                  </p>
                </div>
                <Switch defaultSelected />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Customer Notifications</p>
                  <p className="text-sm text-default-500">
                    Receive notifications about new registered customers
                  </p>
                </div>
                <Switch defaultSelected />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Inventory Alerts</p>
                  <p className="text-sm text-default-500">
                    Receive alerts when products are below the minimum level
                  </p>
                </div>
                <Switch defaultSelected />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Weekly Reports</p>
                  <p className="text-sm text-default-500">
                    Receive weekly reports on sales and performance
                  </p>
                </div>
                <Switch />
              </div>
            </div>
            <Divider />
            <p className="text-sm text-default-500 italic">
              The notification settings will be updated in a future version of
              the system.
            </p>
          </Card>
        </Tab>
        <Tab key="system" title="System">
          <Card className="p-6 mt-4 space-y-4">
            <h2 className="text-xl font-semibold mb-4">System Settings</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Dark Mode</p>
                  <p className="text-sm text-default-500">
                    Toggle between light and dark mode
                  </p>
                </div>
                <Switch
                  isSelected={theme === "dark"}
                  onChange={() =>
                    setTheme(theme === "light" ? "dark" : "light")
                  }
                />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Autosave</p>
                  <p className="text-sm text-default-500">
                    Automatically save in-progress orders
                  </p>
                </div>
                <Switch defaultSelected />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Session Timeout</p>
                  <p className="text-sm text-default-500">
                    Time before session expires
                  </p>
                </div>
                <select className="bg-default-100 border border-default-200 rounded-md px-3 py-1">
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="60" selected>
                    1 hour
                  </option>
                  <option value="120">2 hours</option>
                </select>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Automatic Closure</p>
                  <p className="text-sm text-default-500">
                    Automatically close completed orders after
                  </p>
                </div>
                <select className="bg-default-100 border border-default-200 rounded-md px-3 py-1">
                  <option value="1">1 day</option>
                  <option value="3" selected>
                    3 days
                  </option>
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                </select>
              </div>
            </div>
            <Divider />
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Maintenance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button
                  color="warning"
                  startContent={<Clock size={16} />}
                  onPress={() => alert("This feature will be implemented soon")}
                >
                  Clear Cache
                </Button>
                <Button
                  color="danger"
                  startContent={<Trash size={16} />}
                  onPress={() => alert("This feature will be implemented soon")}
                >
                  Delete Temporary Data
                </Button>
              </div>
            </div>
            <Divider />
            <p className="text-sm text-default-500 italic">
              Some system options will be available in future updates.
            </p>
          </Card>
        </Tab>
      </Tabs>
      {/* Modal para editar horario del día */}
      <Modal isOpen={timeSlotModal.isOpen} onClose={timeSlotModal.onClose}>
        <ModalContent>
          <ModalHeader>
            Edit Schedule -{" "}
            {editingDay.charAt(0).toUpperCase() + editingDay.slice(1)}
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium mb-2">Delivery Hours</h5>
                  <div className="flex gap-2">
                    <Input
                      type="time"
                      label="Opens"
                      value={
                        restaurantConfig.hours_of_operation[editingDay]?.open ||
                        ""
                      }
                      onChange={(e) =>
                        handleTimeChange(editingDay, "open", e.target.value)
                      }
                    />
                    <Input
                      type="time"
                      label="Closes"
                      value={
                        restaurantConfig.hours_of_operation[editingDay]
                          ?.close || ""
                      }
                      onChange={(e) =>
                        handleTimeChange(editingDay, "close", e.target.value)
                      }
                    />
                  </div>
                </div>
                <div>
                  <h5 className="font-medium mb-2">Pickup Hours</h5>
                  <div className="flex gap-2">
                    <Input
                      type="time"
                      label="Opens"
                      value={
                        restaurantConfig.pickup_hours?.[editingDay]?.open || ""
                      }
                      onChange={(e) =>
                        handlePickupTimeChange(
                          editingDay,
                          "open",
                          e.target.value,
                        )
                      }
                    />
                    <Input
                      type="time"
                      label="Closes"
                      value={
                        restaurantConfig.pickup_hours?.[editingDay]?.close || ""
                      }
                      onChange={(e) =>
                        handlePickupTimeChange(
                          editingDay,
                          "close",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={timeSlotModal.onClose}>
              Cancel
            </Button>
            <Button color="primary" onPress={timeSlotModal.onClose}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Modal para añadir día especial */}
      <Modal isOpen={specialDayModal.isOpen} onClose={specialDayModal.onClose}>
        <ModalContent>
          <ModalHeader>Add Special Day</ModalHeader>
          <ModalBody>
            {editingSpecialDay && (
              <div className="space-y-4">
                <Input
                  type="date"
                  label="Date"
                  value={editingSpecialDay.date}
                  onChange={(e) =>
                    setEditingSpecialDay({
                      ...editingSpecialDay,
                      date: e.target.value,
                    })
                  }
                />
                <div className="flex items-center justify-between">
                  <span>Restaurant Closed</span>
                  <Switch
                    isSelected={editingSpecialDay.isClosed}
                    onChange={() =>
                      setEditingSpecialDay({
                        ...editingSpecialDay,
                        isClosed: !editingSpecialDay.isClosed,
                      })
                    }
                  />
                </div>
                {!editingSpecialDay.isClosed && (
                  <>
                    <div>
                      <h5 className="font-medium mb-2">Schedule Delivery</h5>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="time"
                          label="Opens"
                          value={editingSpecialDay.open || ""}
                          onChange={(e) =>
                            setEditingSpecialDay({
                              ...editingSpecialDay,
                              open: e.target.value,
                            })
                          }
                        />
                        <Input
                          type="time"
                          label="Closes"
                          value={editingSpecialDay.close || ""}
                          onChange={(e) =>
                            setEditingSpecialDay({
                              ...editingSpecialDay,
                              close: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <h5 className="font-medium mb-2">Schedule Pickup</h5>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="time"
                          label="Opens"
                          value={editingSpecialDay.pickup_open || ""}
                          onChange={(e) =>
                            setEditingSpecialDay({
                              ...editingSpecialDay,
                              pickup_open: e.target.value,
                            })
                          }
                        />
                        <Input
                          label="Cierra"
                          type="time"
                          value={editingSpecialDay.pickup_close || ""}
                          onChange={(e) =>
                            setEditingSpecialDay({
                              ...editingSpecialDay,
                              pickup_close: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={specialDayModal.onClose}>
              Cancelar
            </Button>
            <Button color="primary" onPress={handleSaveSpecialDay}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default SettingsPage;
